import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiCog, HiLocationMarker, HiChevronRight } from 'react-icons/hi';
import { FaWhatsapp, FaRobot } from 'react-icons/fa';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { SERVICES } from '../utils/constants';

const iconComponents = {
  WhatsApp: FaWhatsapp,
  Robot: FaRobot,
  Target: HiLocationMarker,
  Cog: HiCog,
};

const ServicesSection = () => {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section
      id="services"
      ref={sectionRef}
      style={{
        backgroundColor: '#F5F0E8',
        padding: 'clamp(4rem, 8vw, 7rem) 1.5rem',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{ textAlign: 'center', marginBottom: 'clamp(3rem, 5vw, 5rem)' }}
        >
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: '#1A1A1A',
            marginBottom: '1rem',
            fontFamily: '"DM Sans", sans-serif',
          }}>
            What we build
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#666666',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.65,
            fontFamily: '"DM Sans", sans-serif',
          }}>
            Not generic tools. Systems designed around your workflow, your customers, your language.
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '1.5rem',
        }} className="services-grid">
          {SERVICES.map((service, index) => {
            const Icon = iconComponents[service.icon];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1, ease: 'easeOut' }}
              >
                <Link
                  to={`/services#${service.id}`}
                  style={{
                    display: 'block',
                    background: '#FFFFFF',
                    border: '1px solid var(--color-border)',
                    borderRadius: '16px',
                    padding: 'clamp(1.5rem, 3vw, 2rem)',
                    textDecoration: 'none',
                    transition: 'box-shadow 0.2s',
                    height: '100%',
                  }}
                  className="service-card"
                >
                  {/* Top row: icon + popular badge */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <Icon size={24} color="#1A1A1A" />
                    {service.popular && (
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        padding: '4px 10px',
                        backgroundColor: '#1A1A1A',
                        color: '#FFFFFF',
                        borderRadius: '999px',
                        fontFamily: '"DM Sans", sans-serif',
                      }}>
                        {service.tag}
                      </span>
                    )}
                  </div>

                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: '#1A1A1A',
                    marginBottom: '0.5rem',
                    fontFamily: '"DM Sans", sans-serif',
                  }}>
                    {service.title}
                  </h3>

                  <p style={{
                    fontSize: '0.9rem',
                    lineHeight: 1.65,
                    color: '#666666',
                    marginBottom: '1.25rem',
                    fontFamily: '"DM Sans", sans-serif',
                  }}>
                    {service.description}
                  </p>

                  {/* Metadata rows */}
                  <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1rem', marginTop: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#999999', fontFamily: '"DM Sans", sans-serif' }}>
                        Setup
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1A1A1A', fontFamily: '"DM Sans", sans-serif' }}>
                        {service.pricing.setup}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#999999', fontFamily: '"DM Sans", sans-serif' }}>
                        Monthly
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1A1A1A', fontFamily: '"DM Sans", sans-serif' }}>
                        {service.pricing.monthly}
                      </span>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginTop: '1.25rem',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#1A1A1A',
                    fontFamily: '"DM Sans", sans-serif',
                  }}>
                    Learn more <HiChevronRight size={16} />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      <style>{`
        .service-card:hover {
          box-shadow: 0 4px 24px rgba(0,0,0,0.08) !important;
        }
        @media (min-width: 768px) {
          .services-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </section>
  );
};

export default ServicesSection;
